/**
 * Temporary ID generation utilities
 *
 * All temporary IDs use the prefix "temp-" to be easily identifiable
 * and distinguish them from real UUIDs from the database.
 */

/**
 * Generate a temporary ID with the "temp-" prefix
 * Format: temp-{timestamp}-{random}
 */
export function generateTempId(prefix?: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  const fullPrefix = prefix ? `temp-${prefix}` : "temp";
  return `${fullPrefix}-${timestamp}-${random}`;
}

/**
 * Check if an ID is a temporary ID (not a UUID)
 */
export function isTempId(id: string): boolean {
  return id.startsWith("temp-");
}

/**
 * Check if an ID is a real UUID from the database
 */
export function isUUID(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/**
 * Check if an ID needs to be created in the database
 * (not a UUID = temp ID or other generated ID)
 */
export function needsCreation(id: string): boolean {
  return !isUUID(id);
}

/**
 * Generate a permanent ID with a custom prefix
 * Format: {prefix}-{timestamp}-{random}
 *
 * This is a PERMANENT identifier (not temporary) used for entities
 * stored in JSONB fields (advancementConditions, callDatas, alerts, etc.)
 * These IDs are never replaced and stay forever in the database.
 *
 * @param prefix - The prefix for the ID (e.g., 'advancement', 'info', 'alert')
 */
export function generatePermanentId(prefix: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${prefix}-${timestamp}-${random}`;
}

/**
 * Generate a permanent phase key for a new phase
 * Format: phase-{timestamp}-{random}
 *
 * This is a PERMANENT identifier that stays with the phase forever,
 * even after the temporary ID is replaced by a UUID from the database.
 * Used for versioning, traceability, and GDPR compliance.
 */
export function generatePhaseKey(): string {
  return generatePermanentId("phase");
}
